<script>
import { mapGetters } from 'vuex';
import Avatar from 'next/avatar/Avatar.vue';
import Spinner from 'shared/components/Spinner.vue';
import WootButton from 'dashboard/components/ui/WootButton.vue';

export default {
  name: 'ForwardConversationModal',
  components: {
    Avatar,
    Spinner,
    WootButton,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    isForwarding: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['close', 'forward'],
  data() {
    return {
      searchQuery: '',
      selectedConversationIds: [],
      isLoading: false,
    };
  },
  computed: {
    ...mapGetters({
      conversations: 'getAllConversations',
      currentConversationId: 'getSelectedChat',
    }),
    filteredConversations() {
      const query = this.searchQuery.toLowerCase().trim();

      return this.conversations
        .filter(conv => {
          // Exclude current conversation
          if (conv.id === this.currentConversationId?.id) return false;

          if (!query) return true;

          const senderName = conv.meta?.sender?.name?.toLowerCase() || '';
          const senderPhone = conv.meta?.sender?.phone_number || '';
          const convId = String(conv.id);

          return (
            senderName.includes(query) ||
            senderPhone.includes(query) ||
            convId.includes(query)
          );
        })
        .slice(0, 50);
    },
  },
  watch: {
    show(newVal) {
      if (newVal) {
        this.selectedConversationIds = [];
        this.searchQuery = '';
      }
    },
  },
  methods: {
    onClose() {
      this.$emit('close');
    },
    handleForward() {
      this.$emit('forward', this.selectedConversationIds);
    },
    getLastMessagePreview(conversation) {
      const lastMessage = conversation.messages?.[0];
      if (!lastMessage) return '';

      if (lastMessage.content) {
        const truncated = lastMessage.content.substring(0, 50);
        return lastMessage.content.length > 50 ? `${truncated}...` : truncated;
      }

      if (lastMessage.attachments?.length > 0) {
        const type = lastMessage.attachments[0].file_type;
        return `[${type}]`;
      }

      return '';
    },
  },
};
</script>

<template>
  <woot-modal :show="show" :on-close="onClose">
    <div class="flex flex-col h-[500px]">
      <woot-modal-header
        :header-title="$t('CONVERSATION.FORWARD.TITLE')"
        :header-content="$t('CONVERSATION.FORWARD.SUBTITLE')"
      />

      <div class="flex-1 overflow-hidden flex flex-col px-8 pb-8">
        <!-- Search input -->
        <div class="mb-4">
          <input
            v-model="searchQuery"
            type="text"
            class="w-full px-3 py-2 border border-n-weak rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-woot-500 focus:border-transparent"
            :placeholder="$t('CONVERSATION.FORWARD.SEARCH_PLACEHOLDER')"
          />
        </div>

        <!-- Conversations list -->
        <div class="flex-1 overflow-y-auto border border-n-weak rounded-lg">
          <div v-if="isLoading" class="flex items-center justify-center h-full">
            <Spinner size="large" />
          </div>

          <div
            v-else-if="filteredConversations.length === 0"
            class="flex items-center justify-center h-full text-n-slate-11"
          >
            {{ $t('CONVERSATION.FORWARD.NO_CONVERSATIONS') }}
          </div>

          <div v-else class="divide-y divide-n-weak">
            <label
              v-for="conversation in filteredConversations"
              :key="conversation.id"
              class="flex items-center gap-3 p-3 cursor-pointer hover:bg-n-alpha-1 transition-colors"
            >
              <input
                v-model="selectedConversationIds"
                type="checkbox"
                :value="conversation.id"
                class="w-4 h-4 rounded border-n-strong text-woot-500 focus:ring-woot-500"
              />

              <Avatar
                :src="conversation.meta?.sender?.thumbnail || ''"
                :name="conversation.meta?.sender?.name || 'Unknown'"
                :size="32"
              />

              <div class="flex-1 min-w-0">
                <div class="flex items-center gap-2">
                  <span class="font-medium text-sm text-n-slate-12 truncate">
                    {{ conversation.meta?.sender?.name || 'Unknown' }}
                  </span>
                  <span
                    v-if="conversation.meta?.sender?.phone_number"
                    class="text-xs text-n-slate-11"
                  >
                    {{ conversation.meta?.sender?.phone_number }}
                  </span>
                </div>
                <p class="text-xs text-n-slate-11 truncate mt-0.5">
                  {{ getLastMessagePreview(conversation) }}
                </p>
              </div>

              <span class="text-xs text-n-slate-11 shrink-0">
                #{{ conversation.id }}
              </span>
            </label>
          </div>
        </div>

        <!-- Footer -->
        <div
          class="flex items-center justify-between mt-4 pt-4 border-t border-n-weak"
        >
          <span class="text-sm text-n-slate-11">
            {{
              $t('CONVERSATION.FORWARD.FORWARD_TO_COUNT', {
                count: selectedConversationIds.length,
              })
            }}
          </span>

          <div class="flex gap-2">
            <WootButton
              variant="clear"
              color-scheme="secondary"
              @click="onClose"
            >
              {{ $t('CONVERSATION.FORWARD.BUTTON_CANCEL') }}
            </WootButton>

            <WootButton
              color-scheme="primary"
              :disabled="selectedConversationIds.length === 0"
              :is-loading="isForwarding"
              @click="handleForward"
            >
              {{ $t('CONVERSATION.FORWARD.BUTTON_FORWARD') }}
            </WootButton>
          </div>
        </div>
      </div>
    </div>
  </woot-modal>
</template>
